/**
 * WsBridgeProvider — React context that manages the WsBridge lifecycle.
 *
 * Mounts inside AuthProvider; creates the bridge only when the user is
 * authenticated. On unmount (logout or tab close) it closes the socket.
 *
 * Reconnect-throttle: if the bridge enters 'reconnecting' state more than
 * MAX_RECONNECTS_IN_WINDOW times within RECONNECT_WINDOW_MS, we force-close
 * the bridge to prevent infinite reconnect loops (e.g. when the /ws/ticket
 * endpoint is persistently broken).
 *
 * Consumers:
 *   - `useWsBridge()` — returns the bridge instance (or null before auth).
 *   - `useWsStatus()` — returns the current WsStatus ('idle' | 'connecting' | …).
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useChatStream } from '@/stores/chatStream.store';
import { useMemoryStreamStore } from '@/stores/memoryStream.store';
import { useObservations } from '@/stores/observations.store';
import { createWsBridge, type WsBridge, type WsStatus } from './socket';

/** How many 'reconnecting' status transitions are allowed in the window. */
const MAX_RECONNECTS_IN_WINDOW = 3;
/** Time window (ms) in which reconnect count is tracked. */
const RECONNECT_WINDOW_MS = 10_000;

// -- Context ------------------------------------------------------------------

interface WsBridgeContextValue {
  bridge: WsBridge | null;
  status: WsStatus;
}

// Exported for useWsSession's graceful no-throw access pattern.
export const WsBridgeContext = createContext<WsBridgeContextValue | undefined>(
  undefined,
);

// -- Provider -----------------------------------------------------------------

export function WsBridgeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<WsStatus>('idle');
  const bridgeRef = useRef<WsBridge | null>(null);
  const [, forceRender] = useState(0);

  // Reconnect-throttle state: timestamps of recent 'reconnecting' transitions.
  const reconnectTimestamps = useRef<number[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (bridgeRef.current) {
        bridgeRef.current.close();
        bridgeRef.current = null;
        setStatus('idle');
        forceRender((n) => n + 1);
      }
      return;
    }

    // StrictMode guard: don't recreate if already initialized.
    if (bridgeRef.current) return;

    const newBridge = createWsBridge({
      onEvent: (event) => {
        if (event.type === 'memory.extracted' || event.type === 'memory.promoted') {
          useMemoryStreamStore.getState().applyEvent(event);
          return;
        }
        if (event.type === 'agent.observation') {
          useObservations.getState().add({
            id: event.payload.observationId,
            agentId: event.agentId,
            text: event.payload.text,
            ts: event.ts,
          });
          return;
        }
        useChatStream.getState().applyEvent(event);
      },
      onStatusChange: (next) => {
        setStatus(next);

        if (next === 'reconnecting') {
          const now = Date.now();
          reconnectTimestamps.current = reconnectTimestamps.current.filter(
            (ts) => now - ts < RECONNECT_WINDOW_MS,
          );
          reconnectTimestamps.current.push(now);

          if (reconnectTimestamps.current.length > MAX_RECONNECTS_IN_WINDOW) {
            console.warn(
              '[WsBridgeProvider] Reconnect throttle triggered: bridge reconnected ' +
                `${reconnectTimestamps.current.length} times in ${RECONNECT_WINDOW_MS}ms. ` +
                'Closing bridge to prevent infinite loop.',
            );
            newBridge.close();
            bridgeRef.current = null;
            setStatus('closed');
            reconnectTimestamps.current = [];
            forceRender((n) => n + 1);
          }
        }
      },
    });

    bridgeRef.current = newBridge;
    forceRender((n) => n + 1);

    return () => {
      // Only cleanup on true unmount (not StrictMode re-run).
      // StrictMode calls cleanup then re-runs effect; we guard with
      // the bridgeRef.current check above to avoid re-creating.
    };
  }, [isAuthenticated]);

  // Cleanup on actual unmount
  useEffect(() => {
    return () => {
      if (bridgeRef.current) {
        bridgeRef.current.close();
        bridgeRef.current = null;
      }
    };
  }, []);

  return (
    <WsBridgeContext.Provider value={{ bridge: bridgeRef.current, status }}>
      {children}
    </WsBridgeContext.Provider>
  );
}

// -- Hooks --------------------------------------------------------------------

/** Access the WsBridge instance. Returns null when not yet authenticated. */
export function useWsBridge(): WsBridge | null {
  const ctx = useContext(WsBridgeContext);
  if (ctx === undefined) {
    throw new Error('useWsBridge must be used within a WsBridgeProvider');
  }
  return ctx.bridge;
}

/** The current WebSocket connection status (reactive). */
export function useWsStatus(): WsStatus {
  const ctx = useContext(WsBridgeContext);
  if (ctx === undefined) {
    throw new Error('useWsStatus must be used within a WsBridgeProvider');
  }
  return ctx.status;
}
